import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Megaphone, Send, Eye } from "lucide-react";

const CLUBS = ["Analistas", "Diplomatas", "Sentinelas", "Exploradores"];

/**
 * BroadcastPanel — Admin envia um comunicado à comunidade filtrando por
 * role (all/consultor/coach/staff), clube MBTI e equipa KW.
 * Inclui dry-run para pré-visualizar destinatários.
 */
export default function BroadcastPanel() {
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({
    subject: "",
    message_html: "",
    target_role: "all",
    target_club: "",
    target_team_id: "",
    include_bench: true,
  });
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get("/teams").then((r) => setTeams(r.data)).catch(() => {}); }, []);

  const buildPayload = (dry = false) => ({
    subject: form.subject.trim(),
    message_html: form.message_html.trim(),
    target_role: form.target_role,
    target_club: form.target_club || null,
    target_team_id: form.target_team_id || null,
    include_bench: !!form.include_bench,
    dry_run: dry,
  });

  const preview = async () => {
    if (!form.subject.trim() || !form.message_html.trim()) {
      toast.error("Preenche assunto e mensagem antes de pré-visualizar");
      return;
    }
    setPreviewing(true); setPreviewData(null);
    try {
      const { data } = await api.post("/email/broadcast", buildPayload(true));
      setPreviewData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setPreviewing(false);
  };

  const send = async () => {
    if (!form.subject.trim() || !form.message_html.trim()) {
      toast.error("Preenche assunto e mensagem"); return;
    }
    if (!previewData) {
      toast.error("Pré-visualiza primeiro para confirmar destinatários"); return;
    }
    if (!window.confirm(
      `Enviar comunicado a ${previewData.count} destinatário${previewData.count === 1 ? "" : "s"}?\n\n` +
      `Assunto: "${form.subject}"\n\nEsta acção não pode ser desfeita.`
    )) return;
    setSending(true);
    try {
      const { data } = await api.post("/email/broadcast", buildPayload(false));
      toast.success(`Enviado a ${data.sent}/${data.total} destinatário${data.total === 1 ? "" : "s"}`);
      if (data.failed?.length) toast.error(`${data.failed.length} falha(s): ${data.failed[0].email}…`);
      setForm({ ...form, subject: "", message_html: "" });
      setPreviewData(null);
    } catch (err) { toast.error(formatApiError(err)); }
    setSending(false);
  };

  return (
    <div className="kw-card p-6 max-w-3xl" data-testid="broadcast-panel">
      <div className="flex items-center gap-3 mb-4">
        <Megaphone className="w-5 h-5 text-[#E50000]" />
        <div>
          <div className="kw-overline">Admin</div>
          <h2 className="font-display text-xl font-black uppercase">Enviar Comunicado à Comunidade</h2>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="kw-overline block mb-1 text-[10px]">Destinatários (role)</label>
          <select className="kw-input" value={form.target_role}
            onChange={(e) => setForm({ ...form, target_role: e.target.value })}
            data-testid="broadcast-role">
            <option value="all">Toda a comunidade</option>
            <option value="consultant">Só Consultores</option>
            <option value="coach">Só Secretários</option>
            <option value="staff">Só Staff</option>
          </select>
        </div>
        <div>
          <label className="kw-overline block mb-1 text-[10px]">Clube (opcional)</label>
          <select className="kw-input" value={form.target_club}
            onChange={(e) => setForm({ ...form, target_club: e.target.value })}
            data-testid="broadcast-club">
            <option value="">Todos</option>
            {CLUBS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="kw-overline block mb-1 text-[10px]">Equipa KW (opcional)</label>
          <select className="kw-input" value={form.target_team_id}
            onChange={(e) => setForm({ ...form, target_team_id: e.target.value })}
            data-testid="broadcast-team">
            <option value="">Todas</option>
            {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={!!form.include_bench}
              onChange={(e) => setForm({ ...form, include_bench: e.target.checked })}
              data-testid="broadcast-bench" />
            <span className="text-[#A3A3A3]">Incluir consultores em Bench</span>
          </label>
        </div>
      </div>

      <div className="mb-3">
        <label className="kw-overline block mb-1 text-[10px]">Assunto</label>
        <input className="kw-input" placeholder="Ex: Nova regra: bónus de fim de trimestre"
          value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })}
          data-testid="broadcast-subject" />
      </div>
      <div className="mb-4">
        <label className="kw-overline block mb-1 text-[10px]">Mensagem (HTML permitido: &lt;p&gt;, &lt;strong&gt;, &lt;a&gt;, &lt;ul&gt;)</label>
        <textarea className="kw-input min-h-[160px] font-mono text-xs"
          placeholder="<p>Olá equipa,</p><p>A partir de segunda...</p>"
          value={form.message_html} onChange={(e) => setForm({ ...form, message_html: e.target.value })}
          data-testid="broadcast-message" />
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={preview} disabled={previewing} className="kw-btn-secondary text-sm inline-flex items-center gap-1.5"
          data-testid="broadcast-preview">
          <Eye className="w-4 h-4" /> {previewing ? "A calcular…" : "Pré-visualizar destinatários"}
        </button>
        <button onClick={send} disabled={sending || !previewData} className="kw-btn-primary text-sm inline-flex items-center gap-1.5"
          data-testid="broadcast-send">
          <Send className="w-4 h-4" /> {sending ? "A enviar…" : "Enviar"}
        </button>
      </div>

      {previewData && (
        <div className="mt-4 p-3 border border-[#00FF88]/30 bg-[#00FF88]/5 text-sm" data-testid="broadcast-preview-result">
          <div className="text-[#00FF88] font-bold uppercase text-[10px] tracking-widest">
            {previewData.count} destinatário{previewData.count === 1 ? "" : "s"} corresponde{previewData.count === 1 ? "" : "m"} aos filtros
          </div>
          <div className="text-[10px] text-[#737373] mt-2 break-all">
            Primeiros 10: {previewData.recipients.slice(0, 10).join(", ")}
            {previewData.recipients.length > 10 && "…"}
          </div>
        </div>
      )}

      <p className="text-[10px] text-[#737373] border-t border-[#222] pt-3 mt-4">
        💡 O comunicado é enviado a partir de <strong>lead@kwgrowthleague.com</strong> com o template branded da liga.
      </p>
    </div>
  );
}
